// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { FINANCE, LEASE, PaymentScheduleTypes } from '@makemydeal/dr-platform-types';
import type { OfferType } from '@makemydeal/dr-platform-types';
import type { ReduxMiddleware, ReduxNext, ReduxStore } from '@makemydeal/dr-shared-store';
import { featureToggleSelectors, initActionTypes } from '@makemydeal/dr-shared-store';
import {
    LOCKED_PROGRAM_META,
    PaymentGridSource,
    actionCreators as offerReduxActionCreators,
    actions as offerReduxActionTypes,
    constants as offerReduxConstants,
    utils as offerReduxUtils
} from '@makemydeal/dr-offer-redux';

// actions
import {
    CALC_ENDPOINT_OWNED_BY_DISPATCHER_META,
    PROGRAM_QUOTES_REFETCH_META,
    releaseProgramFallbackValues,
    fallBackToUserProgramQuotes,
    holdProgramFallbackValues,
    invalidateProgramAndRefetch,
    markProgramLookupPending,
    resolveProgramLookup,
    discardProgramLookupPending
} from '../actions/programOrchestrationActionCreators';
import { updateBuyRateOverride, updateSellRateOverride } from '../actions/offerRedux';

// action types
import { PAYMENT_FAILURE, PAYMENT_RECEIVED } from '../actionTypes/deskingActionTypes';

// constants / selectors
import { CALC_ENDPOINTS } from '../constants';
import { KEEP_LENDER_META, invalidatesProgram, isShopperZipChange } from '../programInvalidatingActions';
import { isFnIDealMode } from '../selectors/dealModeSelectors';
import { getIsAutoSelectChecked, getResolvedAutoSelectChecked } from '../selectors/manualFieldOverridesSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';
import { getAcqFeeToCarry, getDealerInputsToCarry } from '../selectors/offerSelectors';
import { getExplicitDealProgram, getProgramLookupPendingRequestId, isProgramLookupPending } from '../selectors/programsSelectors';

// shared
import { CALC_REQUEST_ID_META, markStalePaymentResponse, stampRequestId } from './__common__/calcRequestId';
import { isPaymentGridOriginated } from './__common__/paymentGridOrigin';
import {
    hasProgramOnDeal,
    mileageOverrideNeedsUserProgramQuotes,
    overrideKeepsCalculationEndpoint
} from './__common__/pqSupportedOverrides';
import {
    doesLookupHoldRecoveryValues,
    FINANCE_RATE_FALLBACK_META,
    isFinanceRateFallbackOutcome,
    isPqOverrideRecoveryOutcome,
    PQ_OVERRIDE_RECOVERY_META,
    releasePqOverrideRecoveryHold
} from './__common__/pqOverrideRecovery';

export { CALC_REQUEST_ID_META };

const LENDER_OVERRIDE_ACTIONS: ReadonlySet<string> = new Set([
    offerReduxActionTypes.UPDATED_BASE_RESIDUAL_DOLLAR_OVERRIDE,
    offerReduxActionTypes.UPDATED_BASE_RESIDUAL_PERCENT_OVERRIDE,
    offerReduxActionTypes.UPDATED_PROGRAM_RESIDUAL_DOLLAR_OVERRIDE,
    offerReduxActionTypes.UPDATED_PROGRAM_RESIDUAL_PERCENT_OVERRIDE,
    offerReduxActionTypes.UPDATED_SELL_RATE_OVERRIDE,
    offerReduxActionTypes.UPDATED_BUY_RATE_OVERRIDE,
    offerReduxActionTypes.UPDATE_RESIDUAL_TAB_DISPLAY_TYPE
]);

const SUPPORTED_OFFER_TYPES: ReadonlySet<string> = new Set([FINANCE, LEASE]);

// DE761542: a grid rate or residual edit fans out one payment per cash-down row, all sharing this action's
// request id, so stamping it and then bumping the id for the UPQ fallback below marked every sibling row stale
// and discarded its response. Left unstamped, siblings can no longer supersede each other, which is also what
// removes the need to release those rows' loaders after the fact. Only the stamping is skipped - the trigger
// below still runs.
const STAMP_EXEMPT_ACTIONS: ReadonlySet<string> = new Set([
    offerReduxActionTypes.UPDATED_SELL_RATE_OVERRIDE,
    offerReduxActionTypes.UPDATED_BASE_RESIDUAL_DOLLAR_OVERRIDE,
    offerReduxActionTypes.UPDATED_BASE_RESIDUAL_PERCENT_OVERRIDE,
    offerReduxActionTypes.UPDATED_PROGRAM_RESIDUAL_DOLLAR_OVERRIDE,
    offerReduxActionTypes.UPDATED_PROGRAM_RESIDUAL_PERCENT_OVERRIDE
]);

const actionTriggersPayment = (action: AnyFSA): boolean => Boolean(action.meta?.middleware?.payment);

// Either signal turns program maintenance on: the user asking for Auto Select, or the deal actually being
// priced through program-quotes. A recorded uncheck must not suppress it while the endpoint reads
// program-quotes, because a deal-type change force-resets the endpoint without recording a selection, and
// pricing through PQ against a pinned stale program is worse than either mode on its own.
const isAutoSelectOn = (state: StateTree, offerType: OfferType): boolean =>
    getIsAutoSelectChecked(state, offerType) === true ||
    offerReduxSelectors.getCalculationEndpointByOfferType(state, offerType) === CALC_ENDPOINTS.programQuotes;

const isSupportedDeal = (state: StateTree, offerType: OfferType): boolean =>
    SUPPORTED_OFFER_TYPES.has(offerType) && !offerReduxSelectors.isFlexBuyPayment(state);

/** A `PaymentErrorCategory` member; offer-redux's barrel does not re-export the union to type against. */
const NO_VALID_QUOTES = 'NO_VALID_QUOTES';

// Both are only called once `isProgramLookupOutcome` has read through `meta.originalAction.meta`, so those
// are known to be present.
const errorCategoryOf = (action: AnyFSA): string | undefined => action.meta.error?.errorCategory;

const respondingRequestId = (action: AnyFSA): number | undefined => action.meta.originalAction.meta[CALC_REQUEST_ID_META];

const currentTermMonths = (state: StateTree, offerType: OfferType): number | undefined =>
    offerReduxUtils.parseSelectedTermMonths(offerReduxSelectors.getTermIdByOfferType(state, offerType));

/**
 * Overrides that reach `isOursToAct` but do not change what the payment is priced from, so they must
 * not relabel the applied row. A residual dollar/percent switch only changes how the residual is
 * displayed, and the buy rate is the lender's cost rather than the rate the payment prices off.
 */
const PROVENANCE_NEUTRAL_ACTIONS: ReadonlySet<string> = new Set([
    offerReduxActionTypes.UPDATE_RESIDUAL_TAB_DISPLAY_TYPE,
    offerReduxActionTypes.UPDATED_BUY_RATE_OVERRIDE
]);

/**
 * Whether the deal's decision not to look for a program should be recorded against this term.
 *
 * Gated on the **resolved** Auto Select answer rather than this middleware's own `isAutoSelectOn`.
 * The two disagree: `isAutoSelectOn` reads the live endpoint, while the record's only reader,
 * `useTermNotSupported`, resolves `checked ?? dealerDefault`, and that default is a load-time
 * snapshot. A deal that loaded on program-quotes with nothing in `checkedByOfferType` and has since
 * fallen back therefore reads off here and on there. Recording under that disagreement renders a
 * Term-field message for a lookup that never ran, and `LeaseTermField`'s `hasBlockingFailure` then
 * suppresses a genuine payment failure behind it.
 *
 * F&I mode is excluded for the same reason the lookup path excludes it at its own guard below.
 */
const shouldRecordTermNotLenderBacked = (state: StateTree, action: AnyFSA, offerType: OfferType): boolean =>
    !PROVENANCE_NEUTRAL_ACTIONS.has(action.type) && !isFnIDealMode(state) && !getResolvedAutoSelectChecked(state, offerType);

/**
 * Records that this term will not be lender-backed, at the point the deal decides not to look for a
 * program for it. Reached for a term change and for a lender-field override alike: both arrive here
 * only once `isOursToAct` holds, and in Freehand neither will be re-priced against a program, so in
 * both cases the payment stops being the displayed program's. `restoreHeldValues` carries the
 * previous program's rate and residuals forward, so the term prices off a program that does not
 * cover it — the payment AC8 calls estimated rather than lender-backed. Nothing else recorded that,
 * which is why the display had no way to ask.
 *
 * Reuses the settled no-program record rather than adding a parallel flag: a real lookup that later
 * succeeds clears it through `RESOLVE_PROGRAM_LOOKUP`, so it self-heals.
 */
const recordTermNotLenderBacked = (store: ReduxStore<StateTree>, trigger: string, offerType: OfferType): void => {
    markProgramLookupPending(store, trigger, offerType, currentTermMonths(store.getState(), offerType));
    resolveProgramLookup(store, true);
};

/**
 * Stamps the re-derived record with `SELECTED_TERM` rather than whatever action actually caused the
 * no-program outcome before reload - by design, not by accident. `INIT_SUCCESS` clears redux state
 * on load, so there is no trail left to recover the original trigger from; only the current
 * outcome (no program for the current term) survives. `SELECTED_TERM` is the one trigger
 * `useTermNotSupported`'s `TERM_MESSAGE_TRIGGERS` always honors, so this is the scoped-down
 * guarantee: a no-program outcome that was actually caused by a term change is guaranteed correct
 * post-reload. One caused by Annual Miles or a zip change - both excluded from
 * `TERM_MESSAGE_TRIGGERS` for the same reason `useTermNotSupported` excludes them - would show the
 * Term-field message here even though the live lookup path would have suppressed it. Accepted for
 * now; fixing it needs a way to tell which dimension a reloaded no-program outcome belongs to.
 */
const syncNoProgramLookupOnOpen = (store: ReduxStore<StateTree>): void => {
    const state = store.getState();
    const offerType = offerReduxSelectors.getCurrentOfferType(state);
    if (!isSupportedDeal(state, offerType)) return;
    if (isFnIDealMode(state)) return;
    if (!getResolvedAutoSelectChecked(state, offerType)) return;

    const programFound = Boolean(offerReduxSelectors.getSelectedTermByOfferType(state, offerType)?.lenderProgram?.programName);
    if (programFound) return;

    recordTermNotLenderBacked(store, offerReduxActionTypes.SELECTED_TERM, offerType);
};

const isProgramLookupOutcome = (action: AnyFSA): boolean =>
    Boolean(action.meta?.originalAction?.meta?.[PROGRAM_QUOTES_REFETCH_META]);

const lookupEditOf = (action: AnyFSA): AnyFSA | undefined => {
    const { type, payload } = action.meta.originalAction;
    return type === offerReduxActionTypes.UPDATE_CURRENT_CALCULATION_ENDPOINT ? undefined : { type, payload };
};

const carriesOnePaySwitchCashDown = (state: StateTree, offerType: OfferType): boolean =>
    offerType === LEASE &&
    featureToggleSelectors.isOnePaySwitchCapReductionCashEnabled(state) &&
    !featureToggleSelectors.isEquityAllocationOrderEnabled(state);

const isOnePaySwitch = (action: AnyFSA): boolean =>
    action.type === offerReduxActionTypes.UPDATED_PAYMENT_FREQUENCY &&
    action.payload.frequency === PaymentScheduleTypes.OnePayLease;

const leaseCashDownOverrideOf = (action: AnyFSA): number | undefined => {
    const downPayment = action.meta[offerReduxConstants.PAYMENT_REQUEST_OVERRIDE_NODE]?.[LEASE]?.downPayment;
    return typeof downPayment === 'number' ? downPayment : undefined;
};

const onePaySwitchCashDownOf = (state: StateTree, action: AnyFSA, offerType: OfferType): number | undefined =>
    carriesOnePaySwitchCashDown(state, offerType) && isOnePaySwitch(action) ? leaseCashDownOverrideOf(action) : undefined;

const onePayLookupCashDownOf = (state: StateTree, outcome: AnyFSA, offerType: OfferType): number | undefined => {
    if (!carriesOnePaySwitchCashDown(state, offerType)) return undefined;
    const lookup = outcome.meta.originalAction;
    const isOnePayLookup = isOnePaySwitch(lookup) || lookup.type === offerReduxActionTypes.UPDATE_CURRENT_CALCULATION_ENDPOINT;
    return isOnePayLookup ? leaseCashDownOverrideOf(lookup) : undefined;
};

const withCashDown = (downPayment: number | undefined) => (downPayment === undefined ? {} : { switchMeta: { downPayment } });

const isFinanceRateFallback = (state: StateTree, action: AnyFSA, offerType: OfferType): boolean =>
    offerType === FINANCE &&
    action.type === offerReduxActionTypes.UPDATED_SELL_RATE_OVERRIDE &&
    featureToggleSelectors.isFinanceRateFallbackEnabled(state) &&
    !isPaymentGridOriginated(action) &&
    hasProgramOnDeal(state, offerType);

type SelectedTermRates = { months: number; sellRate?: number; buyRate?: number };

const readSelectedTermRates = (state: StateTree, offerType: OfferType, months: number): SelectedTermRates => ({
    months,
    sellRate: offerReduxSelectors.getSellRateOverrideByOfferTypeAndTerm(state, offerType, months),
    buyRate: offerReduxSelectors.getBuyRateOverrideByOfferTypeAndTerm(state, offerType, months)
});

const writeSelectedTermRates = (store: ReduxStore<StateTree>, { months, sellRate, buyRate }: SelectedTermRates): void => {
    if (sellRate != null) {
        store.dispatch(updateSellRateOverride(sellRate, months, { isSelectedTerm: true, skipPaymentCall: true }));
    }
    if (buyRate != null) {
        store.dispatch(updateBuyRateOverride(buyRate, months, { isSelectedTerm: true, withPayment: false }));
    }
};

const isGridColumnPayment = (action: AnyFSA): boolean => {
    const { gridAction = false, isMainPayment = false } = action.meta?.originalAction?.meta?.paymentGrid || {};
    return gridAction && !isMainPayment;
};

/**
 * Whether the ASP payment-grid redesign is live for this deal. Read through offer-redux's gate - the one the
 * grid itself and dash-components' hook both use - rather than the raw toggle, so this middleware cannot
 * disagree with the grid about which rules are in force. That gate also excludes Flex Buy, and a Lease deal
 * whose Finance node carries the Flex Buy formula runs the pre-redesign grid while the raw toggle reads true.
 */
const isRedesignActive = (state: StateTree, offerType: OfferType): boolean =>
    offerReduxSelectors.isAspPaymentGridRedesignActive(state, offerType);

/**
 * With the grid open, selecting a cell is a choice among cells already priced, each carrying its own engine.
 * Invalidating the deal's program for one refetched programs and re-priced every cell. Only the selection is excluded.
 */
const isGridCellSelection = (state: StateTree, action: AnyFSA, offerType: OfferType): boolean =>
    action.type === offerReduxActionTypes.SELECTED_TERM &&
    offerType === FINANCE &&
    isRedesignActive(state, offerType) &&
    offerReduxSelectors.getPaymentGridToggle(state, offerType);

/**
 * On Lease nothing the grid originates may move the deal's engine or invalidate its program - the grid carries the
 * engine per call. Scoped to the action, not to the grid being open, so a deal-screen edit still decides the engine;
 * see `isPaymentGridOriginated` for why `gridAction` alone is not enough. Deliberately broader than Finance's rule
 * above: exempting only `SELECTED_TERM` would leave a grid rate edit invalidating.
 */
const isLeaseGridOriginated = (state: StateTree, action: AnyFSA, offerType: OfferType): boolean =>
    offerType === LEASE && isRedesignActive(state, offerType) && isPaymentGridOriginated(action);

/**
 * The term a closing grid's own no-program verdict describes, or `undefined` where there is nothing to carry over.
 *
 * With the grid open, the "no lender programs available for a {N}-month term" message beside the Auto Select checkbox
 * can be driven by the GRID's verdict for the deal's column (`getSelectedPaymentGridCellSource`) rather than by a
 * deal-screen program lookup, which a grid column's fallback never runs. Both halves of that verdict die with the grid
 * - the selector is gated on the grid being open, and `RESET_PAYMENT_GRID` clears `columnSources` - so closing on a
 * cell the grid had quoted on user-program-quotes took the message away with them, while the deal it describes had not
 * changed at all.
 *
 * Carried into the settled no-program record the deal screen already reads, under the `SELECTED_TERM` trigger, which
 * `getNoProgramLookupTrigger` reports and `useTermNotSupported` is the only renderer of. Self-healing is the reason for
 * reusing it rather than adding a flag: a later lookup that does find a program clears it through
 * `RESOLVE_PROGRAM_LOOKUP`, and `getNoProgramLookupTrigger` already withholds it once the deal's term moves off the one
 * recorded here.
 *
 * Gated on Auto Select resolving ON, which is what `useTermNotSupported` requires to render at all: with it off no
 * message was on screen to preserve, and recording one would light up `getIsTermEstimatedNotLenderBacked` for a lookup
 * that never ran - the hazard `shouldRecordTermNotLenderBacked` documents.
 *
 * Read BEFORE next(): the toggle reducer runs first, and the selector answers `undefined` for a closed grid.
 */
const gridCloseNoProgramTerm = (state: StateTree, action: AnyFSA, offerType: OfferType): number | undefined => {
    if (action.type !== offerReduxActionTypes.UPDATED_PAYMENT_GRID_TOGGLE) return undefined;
    if (action.payload) return undefined;
    if (!isRedesignActive(state, offerType)) return undefined;
    if (isFnIDealMode(state)) return undefined;
    if (!getResolvedAutoSelectChecked(state, offerType)) return undefined;

    const source = offerReduxSelectors.getSelectedPaymentGridCellSource(state, offerType);
    if (source !== PaymentGridSource.UserProgramQuotes && source !== PaymentGridSource.Unavailable) return undefined;

    return currentTermMonths(state, offerType);
};

export const middleware: ReduxMiddleware<StateTree> = (store: ReduxStore<StateTree>) => {
    const stamp = (action: AnyFSA): number => stampRequestId(store, action);

    return (next: ReduxNext) => (action: AnyFSA) => {
        const stateBefore = store.getState();

        if (!featureToggleSelectors.areFeatureTogglesAvailable(stateBefore)) {
            next(action);
            return;
        }

        if (!featureToggleSelectors.isImprovedAutoSelectEnabled(stateBefore)) {
            next(action);
            return;
        }

        if (action.type === initActionTypes.INIT_SUCCESS) {
            next(action);
            if (featureToggleSelectors.isNoProgramMessagePersistEnabled(stateBefore)) {
                syncNoProgramLookupOnOpen(store);
            }
            return;
        }

        const offerTypeBefore = offerReduxSelectors.getCurrentOfferType(stateBefore);
        // Both read before next(), so each verdict describes the action as dispatched.
        const gridCellSelected = isGridCellSelection(stateBefore, action, offerTypeBefore);
        const isFromLeaseGrid = isLeaseGridOriginated(stateBefore, action, offerTypeBefore);
        const closingGridNoProgramTerm = gridCloseNoProgramTerm(stateBefore, action, offerTypeBefore);

        const isInvalidatingAction = invalidatesProgram(action);
        const isOverrideTrigger =
            LENDER_OVERRIDE_ACTIONS.has(action.type) || mileageOverrideNeedsUserProgramQuotes(stateBefore, action);
        // Stamped even when excluded above: the selection still makes a payment, and latest-call-wins is what
        // stops a slower earlier one landing last and leaving the deal on a cell the user moved off.
        const isPayingTrigger = (isInvalidatingAction || isOverrideTrigger) && actionTriggersPayment(action);
        const isInvalidatingTrigger = isInvalidatingAction && !gridCellSelected;
        // `DealTypeDropdown` moves the endpoint itself for the Cash, FlexBuy and used-vehicle-Lease transitions
        // and its `updatedOfferType` is in the invalidating set, so both switched on one transition. Governs
        // what this middleware does, not whether the action carries a payment - stamping stays on
        // `isPayingTrigger` so a marked transition still supersedes an earlier in-flight one.
        const dispatcherOwnsEndpoint = Boolean(action.meta?.[CALC_ENDPOINT_OWNED_BY_DISPATCHER_META]);
        const isOursToAct = isPayingTrigger && (isInvalidatingTrigger || isOverrideTrigger) && !dispatcherOwnsEndpoint;
        const isTerminalPayment = action.type === PAYMENT_RECEIVED || action.type === PAYMENT_FAILURE;
        const isLookupPendingBefore = isProgramLookupPending(stateBefore, offerTypeBefore);
        const isFinanceRateEdit = isFinanceRateFallback(stateBefore, action, offerTypeBefore);
        const overrideStaysOnProgramQuotes =
            isOursToAct &&
            !isInvalidatingTrigger &&
            !isFromLeaseGrid &&
            isAutoSelectOn(stateBefore, offerTypeBefore) &&
            ((offerTypeBefore === LEASE && !isLookupPendingBefore && overrideKeepsCalculationEndpoint(stateBefore, action)) ||
                isFinanceRateEdit);

        // isPayingTrigger keeps a cell selection stamped; the exempt set governs which triggers stamp at all.
        if (isPayingTrigger && !STAMP_EXEMPT_ACTIONS.has(action.type)) {
            stamp(action);
        }

        // Not held for a Lease grid action or a transition whose dispatcher owns the endpoint - nothing below
        // consumes or releases these for either, and values left held would let a much later fallback price with
        // rates held for an unrelated change.
        const holdsForInvalidation =
            isInvalidatingTrigger && actionTriggersPayment(action) && !isFromLeaseGrid && !dispatcherOwnsEndpoint;
        const isZipChange = isShopperZipChange(action) && featureToggleSelectors.isKeepLenderOnZipChangeEnabled(stateBefore);
        const explicitProgram =
            holdsForInvalidation && action.type !== offerReduxActionTypes.UPDATED_OFFER_TYPE && !isZipChange
                ? getExplicitDealProgram(stateBefore, offerTypeBefore)
                : undefined;
        const lenderProgramToKeep =
            holdsForInvalidation && isZipChange ? getExplicitDealProgram(stateBefore, offerTypeBefore) : undefined;
        const keepLender = lenderProgramToKeep?.lenderId != null && {
            lenderId: lenderProgramToKeep.lenderId,
            programName: lenderProgramToKeep.programName,
            rateProgram: lenderProgramToKeep.rateProgram
        };
        const acqFeeToCarry = explicitProgram && offerTypeBefore === LEASE ? getAcqFeeToCarry(stateBefore) : undefined;
        const dealerInputsToCarry = explicitProgram && offerTypeBefore === LEASE ? getDealerInputsToCarry(stateBefore) : undefined;

        if (holdsForInvalidation) {
            holdProgramFallbackValues(store, stateBefore, offerTypeBefore, explicitProgram, acqFeeToCarry, dealerInputsToCarry);
        }

        if (explicitProgram) {
            action.meta = { ...action.meta, [LOCKED_PROGRAM_META]: explicitProgram };
        }

        if (overrideStaysOnProgramQuotes) {
            if (!isLookupPendingBefore) {
                holdProgramFallbackValues(store, stateBefore, offerTypeBefore);
            }
            action.meta = {
                ...action.meta,
                [PQ_OVERRIDE_RECOVERY_META]: true,
                ...(isFinanceRateEdit && { [FINANCE_RATE_FALLBACK_META]: true })
            };
        }

        if (isTerminalPayment) {
            markStalePaymentResponse(store, action);
        }

        next(action);

        if (action.meta?.isStalePaymentResponse) {
            if (
                featureToggleSelectors.isPreserveFieldsOnPaymentResponseEnabled(stateBefore) &&
                offerTypeBefore === LEASE &&
                isTerminalPayment &&
                isProgramLookupOutcome(action)
            ) {
                const respondingId = respondingRequestId(action);
                if (respondingId !== undefined && getProgramLookupPendingRequestId(store.getState()) === respondingId) {
                    discardProgramLookupPending(store, respondingId);
                    releaseProgramFallbackValues(store);
                }
            }
            return;
        }

        // Ahead of the guards below, none of which this is subject to: it records what the grid already decided rather
        // than deciding anything, and `paymentGridMiddleware` is upstream, so the reset that destroys the evidence runs
        // after this returns.
        if (closingGridNoProgramTerm !== undefined) {
            markProgramLookupPending(store, offerReduxActionTypes.SELECTED_TERM, offerTypeBefore, closingGridNoProgramTerm);
            resolveProgramLookup(store, true);
        }

        const state = store.getState();
        const offerType = offerReduxSelectors.getCurrentOfferType(state);

        if (!isSupportedDeal(state, offerType)) return;

        // The grid carries its columns' engines per call, so nothing it does moves the deal's engine or invalidates its
        // program. Deal-screen edits and responses are untouched.
        if (isFromLeaseGrid) return;

        if (isOursToAct) {
            // A deal-type change is a mode-level event: the deal type being moved to starts on
            // program-quotes even if the previous one was on user-program-quotes. That rule predates this
            // change and was confirmed with product, so it applies whether or not Auto Select was on.
            //
            // Every other invalidating field is an edit *within* a mode. In Freehand the values on screen
            // are the user's, and pulling the deal onto program-quotes would overwrite them (AC1, AC2) —
            // so those only act when Auto Select is already on, or when Explicit Lender P1 has a program
            // on the deal to re-quote.
            const isDealTypeChange = action.type === offerReduxActionTypes.UPDATED_OFFER_TYPE;
            if (!isDealTypeChange && !isAutoSelectOn(state, offerType) && !explicitProgram && !keepLender) {
                if (shouldRecordTermNotLenderBacked(state, action, offerType)) {
                    recordTermNotLenderBacked(store, action.type, offerType);
                }
                return;
            }

            if (isInvalidatingTrigger) {
                if (isFnIDealMode(state)) return;
                let lookupRequestId: number | undefined;
                const captureStamp = (paymentAction: AnyFSA): number => (lookupRequestId = stamp(paymentAction));
                if (
                    invalidateProgramAndRefetch(store, offerType, {
                        stampRequestId: captureStamp,
                        ignoreDealerDefault: !isDealTypeChange,
                        lockedProgram: explicitProgram,
                        ...(keepLender && { keepLender }),
                        keepDealerAcqFee: acqFeeToCarry?.dealerEntered === true,
                        keepDealerSecurityDeposit: dealerInputsToCarry?.securityDeposit != null,
                        ...(explicitProgram || keepLender
                            ? { lookupAction: action }
                            : withCashDown(onePaySwitchCashDownOf(state, action, offerType)))
                    })
                ) {
                    markProgramLookupPending(store, action.type, offerType, currentTermMonths(state, offerType), lookupRequestId);
                    if (keepLender) {
                        store.dispatch(
                            offerReduxActionCreators.clearPaymentGridTermPrograms(offerTypeBefore, [
                                currentTermMonths(stateBefore, offerTypeBefore) as number
                            ])
                        );
                        action.meta = { ...action.meta, [KEEP_LENDER_META]: keepLender };
                    }
                }
            } else if (offerType === LEASE) {
                if (overrideStaysOnProgramQuotes) return;
                // Lease only. A finance rate override already rides the program-quotes payload
                // (`doOverridesExistForOfferType` / `createPaymentOverrides` in paymentPayloadUtils), so it
                // prices correctly without a mode flip — moving finance to user-program-quotes here would be
                // a behaviour change of its own, and would collide with `handleCalculationEngineChange`,
                // which only runs for finance.
                fallBackToUserProgramQuotes(store, offerType, { stampRequestId: stamp, seedLenderFees: true });
            }
            return;
        }

        if (!isTerminalPayment) return;
        if (isGridColumnPayment(action)) return;

        if (isPqOverrideRecoveryOutcome(action)) {
            const isFinanceRecovery = isFinanceRateFallbackOutcome(action, offerType);
            const lookupHoldsValues = doesLookupHoldRecoveryValues(state, action, offerType);
            releasePqOverrideRecoveryHold(store, state, action, offerType);
            if (action.type !== PAYMENT_FAILURE) return;
            if (!isFinanceRecovery) {
                fallBackToUserProgramQuotes(store, offerType, {
                    stampRequestId: stamp,
                    restoreHeldValues: true,
                    seedLenderFees: true
                });
                return;
            }

            const { months: editedMonths, sellRate: rejectedSellRate } = action.meta.originalAction.payload;
            if (currentTermMonths(state, offerType) !== editedMonths) return;

            const rates = readSelectedTermRates(state, offerType, editedMonths);
            if (rates.sellRate !== rejectedSellRate) return;

            const pendingLookupRequestId = getProgramLookupPendingRequestId(state);
            fallBackToUserProgramQuotes(store, offerType, { stampRequestId: stamp, restoreHeldValues: true });
            if (offerReduxSelectors.getIsUserProgramQuotes(store.getState(), offerType)) {
                writeSelectedTermRates(store, rates);
                if (lookupHoldsValues) {
                    releaseProgramFallbackValues(store);
                    discardProgramLookupPending(store, pendingLookupRequestId);
                }
            }
            return;
        }

        if (offerReduxSelectors.getIsUserProgramQuotes(state, offerType)) {
            // The endpoint moved before this lookup's outcome landed, so nothing below will resolve its
            // marker. Discard it rather than leave one standing that no later outcome owns.
            if (isProgramLookupOutcome(action)) discardProgramLookupPending(store, respondingRequestId(action));
            return;
        }
        if (!isProgramLookupOutcome(action)) return;
        // The lookup's outcome is known now, whichever way it went, so the held values are released on every
        // path below. Leaving them would let a fallback much later price with values held for an unrelated
        // earlier change.
        if (action.type === PAYMENT_FAILURE) {
            // Only "no quotes came back" is a no-program outcome; a timed-out or errored call falls back
            // without a message. Read before the fallback, which overwrites the evidence.
            resolveProgramLookup(store, errorCategoryOf(action) === NO_VALID_QUOTES, respondingRequestId(action));
            fallBackToUserProgramQuotes(store, offerType, {
                stampRequestId: stamp,
                restoreHeldValues: true,
                lookupEdit: lookupEditOf(action),
                ...withCashDown(onePayLookupCashDownOf(state, action, offerType))
            });
            releaseProgramFallbackValues(store);
            return;
        }

        const programFound = Boolean(offerReduxSelectors.getSelectedTermByOfferType(state, offerType)?.lenderProgram?.programName);
        resolveProgramLookup(store, !programFound, respondingRequestId(action));
        if (!programFound) {
            fallBackToUserProgramQuotes(store, offerType, {
                stampRequestId: stamp,
                restoreHeldValues: true,
                lookupEdit: lookupEditOf(action),
                ...withCashDown(onePayLookupCashDownOf(state, action, offerType))
            });
        }

        releaseProgramFallbackValues(store);
    };
};
